import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { haptic } from '../../lib/haptic';

type Props = { fetchedAt: number; onRefresh?: () => void | Promise<void> };

function ago(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/**
 * Stale-data banner. Native pull-to-refresh is unavailable in this app
 * (`globals.css` sets `overscroll-behavior: none`, and iOS disables it entirely
 * inside an installed standalone PWA), so when `onRefresh` is supplied the
 * banner IS the refresh control — tap it to re-fetch. Without `onRefresh` it
 * stays a passive status line.
 */
export function StaleBanner({ fetchedAt, onRefresh }: Props) {
  const { t } = useTranslation('common');
  const [busy, setBusy] = useState(false);
  const label = t('stale_data', { time: ago(Date.now() - fetchedAt) });
  const cls =
    'w-full flex items-center justify-center gap-1.5 bg-[var(--status-info-bg)] text-[var(--status-info-fg)] text-sm py-2 px-4 text-center';

  if (!onRefresh) return <div role="status" className={cls}>{label}</div>;

  const handle = async () => {
    if (busy) return;
    haptic.tap();
    setBusy(true);
    try {
      await onRefresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <button type="button" onClick={handle} disabled={busy} className={`${cls} active:opacity-60`}>
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className={busy ? 'animate-spin' : ''}
      >
        <path d="M21 12a9 9 0 1 1-2.6-6.36" />
        <path d="M21 3v6h-6" />
      </svg>
      {label}
    </button>
  );
}
